import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ContextualHelpProps {
  /** Help article ID to link to */
  articleId?: string;
  /** Category to filter help center to */
  category?: string;
  /** Custom tooltip text */
  tooltip?: string;
  /** Size of the icon */
  size?: 'sm' | 'md' | 'lg';
  /** Custom className */
  className?: string;
  /** Open in new tab */
  openInNewTab?: boolean;
}

/**
 * Contextual Help Component
 * 
 * Displays a help icon that links to the Help Center.
 * Can link to a specific article, filter by category, or just open the help center.
 * 
 * Usage examples:
 * - <ContextualHelp articleId="task_create" /> - Links to specific article
 * - <ContextualHelp category="Tasks" /> - Opens help center filtered to Tasks
 * - <ContextualHelp /> - Opens general help center
 */
const ContextualHelp: React.FC<ContextualHelpProps> = ({
  articleId,
  category,
  tooltip,
  size = 'md',
  className = '',
  openInNewTab = false,
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const defaultTooltip = articleId
    ? 'View help article'
    : category
    ? `View ${category} help articles`
    : 'Open Help Center';

  const helpUrl = articleId
    ? `/help?article=${articleId}`
    : category
    ? `/help?category=${encodeURIComponent(category)}`
    : '/help';

  const linkProps = openInNewTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Link
      to={helpUrl}
      {...linkProps}
      className={`inline-flex items-center text-gray-400 hover:text-primary-600 transition-colors ${className}`}
      title={tooltip || defaultTooltip}
      aria-label={tooltip || defaultTooltip}
    >
      <HelpCircle className={sizeClasses[size]} />
    </Link>
  );
};

export default ContextualHelp;

